import { useState, useMemo, useEffect } from 'react';
import { OvColumn } from '../rebalancePage/sharedTypes';

/**
 * Custom hook for managing column visibility.
 *
 * @template T - The type of the data associated with each column.
 *
 * @param {Array<{ header: string, accessor: keyof T,
 * cell?: (row: T) => JSX.Element }>} initialColumns - An array of column definitions. Each column object should have a 'header' and 'accessor' property, and optionally a 'cell' rendering function.
 *
 * @returns {{
 * columnVisibility: Record<keyof T, boolean>;
 * toggleColumnVisibility: (accessor: keyof T) => void;
 * visibleColumns: Array<{ header: string, accessor: keyof T, cell?: (row: T) => JSX.Element }>;
 * }} - An object containing the column visibility state, a toggle function, and the filtered visible columns.
 */
const useTableColumnVisibility = <T extends { [key: string]: any }>(initialColumns: OvColumn<T>[]) => {
  const [currentColumns, setCurrentColumns] = useState<OvColumn<T>[]>(initialColumns);

  useEffect(() => setCurrentColumns(initialColumns), [initialColumns]);

  const [columnVisibility, setColumnVisibility] = useState<Record<keyof T | string, boolean>>(() => (
    currentColumns.reduce((acc, column) => {
      acc[column.accessor] = 'visible' in column ? column.visible || false : true; // All columns visible by default
      return acc;
    }, {} as Record<keyof T | string, boolean>)
  ));

  /**
   * Toggles the visibility of a specific column.
   *
   * @param {keyof T} accessor - The accessor of the column to toggle.
   */
  const toggleColumnVisibility = (accessor: keyof T) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [accessor]: !prev[accessor],
    }));
  };

  const updateColumnVisibility = (columns: OvColumn<T>[]) => (
    setColumnVisibility(
      columns.reduce((acc, column) => {
        acc[column.accessor] = 'visible' in column ? column.visible || false : true; // All columns visible by default
        return acc;
      }, {} as Record<keyof T | string, boolean>),
    )
  );

  /**
   * Filters the initial columns based on the current visibility state.
   */
  const visibleColumns = useMemo(() => (
    currentColumns.filter((column) => columnVisibility[column.accessor])
  ), [currentColumns, columnVisibility]);

  return {
    columnVisibility,
    toggleColumnVisibility,
    updateColumnVisibility,
    visibleColumns,
  };
};

export default useTableColumnVisibility;
